import type {
  DocumentDetail,
  Field,
  FieldKind,
} from '@/api/docfiller-types'
import type { NormalizedProposal } from '../chat-utils'
import { isNoOpFieldChange } from './diff'
import type { FieldEditAction } from './types'

/**
 * Convert a chat-emitted normalised proposal into a field-edit
 * suggestion the review tray can render. The before-value is sourced
 * from `doc.fields[i].current_value` at the moment of conversion — that
 * way the tray's conflict detector has a stable reference point to
 * compare against when the user finally clicks Apply.
 *
 * Returns `null` when the proposal cannot be safely surfaced as a
 * reviewable action (e.g. no field_uuid, or the proposed value equals
 * the current value once humanised — there's nothing for the user to
 * approve in that case).
 */
export function proposalToFieldEditAction(
  proposal: NormalizedProposal,
  doc: Pick<DocumentDetail, 'uuid' | 'fields'>,
): FieldEditAction | null {
  if (!proposal.fieldUuid) return null
  const field = doc.fields.find((f) => f.uuid === proposal.fieldUuid)
  if (!field) return null

  const kind = (proposal.fieldKind ?? field.kind) as FieldKind | null
  // Hide redundant proposals — humanised before == humanised after.
  if (isNoOpFieldChange(field.current_value ?? null, proposal.value, kind)) {
    return null
  }

  return {
    kind: 'field-edit',
    id: `${proposal.runId}:${proposal.fieldUuid}`,
    documentUuid: doc.uuid,
    fieldUuid: proposal.fieldUuid,
    fieldDisplayName: proposal.displayName,
    fieldRawName: proposal.rawFieldName,
    fieldKind: kind,
    before: field.current_value ?? null,
    after: proposal.value,
    context: {
      runId: proposal.runId,
      confidence: proposal.confidence,
      reasoning: proposal.reasoning,
      source: 'Agent (chat)',
    },
  }
}

/**
 * Collect all pending field suggestions from a document's
 * `suggested_value` column. These are proposals that were captured
 * server-side but never explicitly accepted by the user — the most
 * common source for the review tray on document detail pages.
 */
export function fieldsWithPendingSuggestions(
  doc: Pick<DocumentDetail, 'uuid' | 'fields'>,
): FieldEditAction[] {
  const out: FieldEditAction[] = []
  for (const field of doc.fields) {
    const suggestion = field.suggested_value
    if (suggestion == null || suggestion === '') continue
    if (suggestion === field.current_value) continue
    if (isNoOpFieldChange(field.current_value, suggestion, field.kind)) continue
    out.push(fieldToAction(field, doc.uuid))
  }
  return out
}

function fieldToAction(field: Field, documentUuid: string): FieldEditAction {
  const display = field.friendly_name?.trim() || field.name
  return {
    kind: 'field-edit',
    id: `pending:${field.uuid}`,
    documentUuid,
    fieldUuid: field.uuid,
    fieldDisplayName: display,
    fieldRawName: field.name,
    fieldKind: field.kind as FieldKind,
    before: field.current_value ?? null,
    after: field.suggested_value ?? '',
    context: {
      confidence: null,
      reasoning: null,
      source: 'Agent',
    },
  }
}
